import { orderHasExpertAudit } from "@/lib/expert-line-items";
import { createSupabaseBrowserClient } from "@/lib/supabase/client";

export type CustomerOrderRow = {
  id: string;
  created_at: string;
  amount_total_cents: number;
  currency: string;
  status: string;
  line_summary: string;
  fulfillment_note: string | null;
  delivery_url: string | null;
  customer_email: string;
  line_items: unknown;
  metadata: unknown;
};

function isAbsorbedOrder(metadata: unknown): boolean {
  if (!metadata || typeof metadata !== "object" || Array.isArray(metadata)) return false;
  return Boolean((metadata as Record<string, unknown>).absorbed_into_order_id);
}

export async function loadAccountProfilePayload(): Promise<{
  email: string;
  profile: {
    display_name: string | null;
    phone: string | null;
    company_name: string | null;
    notes: string | null;
  };
} | null> {
  const sb = createSupabaseBrowserClient();
  const {
    data: { user },
  } = await sb.auth.getUser();
  if (!user?.id) return null;

  let { data: profile } = await sb.from("profiles").select("*").eq("id", user.id).maybeSingle();
  if (!profile) {
    await sb.from("profiles").insert({ id: user.id });
    const again = await sb.from("profiles").select("*").eq("id", user.id).maybeSingle();
    profile = again.data;
  }

  return {
    email: user.email ?? "",
    profile: {
      display_name: profile?.display_name ?? null,
      phone: profile?.phone ?? null,
      company_name: profile?.company_name ?? null,
      notes: profile?.notes ?? null,
    },
  };
}

export async function loadAccountOrdersPayload(): Promise<{
  orders: CustomerOrderRow[];
  expertStatusByOrder: Record<string, string>;
} | null> {
  const sb = createSupabaseBrowserClient();
  const {
    data: { user },
  } = await sb.auth.getUser();
  if (!user) return null;

  const { data: orders, error } = await sb
    .from("customer_orders")
    .select(
      "id, created_at, amount_total_cents, currency, status, line_summary, fulfillment_note, delivery_url, customer_email, line_items, metadata",
    )
    .order("created_at", { ascending: false });

  if (error) {
    console.error("[account/orders]", error);
    return null;
  }

  const visibleOrders = (orders ?? []).filter((o) => !isAbsorbedOrder(o.metadata)) as CustomerOrderRow[];
  const expertOrderIds = visibleOrders.filter((o) => orderHasExpertAudit(o.line_items)).map((o) => o.id);

  let expertStatusByOrder: Record<string, string> = {};
  if (expertOrderIds.length > 0) {
    const { data: exRows } = await sb
      .from("expert_audio_reviews")
      .select("customer_order_id, status")
      .in("customer_order_id", expertOrderIds);
    expertStatusByOrder = Object.fromEntries((exRows ?? []).map((r) => [r.customer_order_id, r.status]));
  }

  return { orders: visibleOrders, expertStatusByOrder };
}
